import React from 'react';

import {
  SelectField, TextField, useRecordContext,
} from 'react-admin';

import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
} from '@mui/material';

import { get } from 'lodash';

import Answer from '../Answer';

import languages, { Languages } from '../../../../constants/languages';
import booleanChoices from '../../../../constants/booleanChoices';
import { countryChoices } from '../../../../constants/countries';
import { identifierTypeChoices } from '../../../../constants/identifierTypes';
import { SignerParameters } from '../../questionParameters';
import questions from '../../../../constants/questions';
import applyChoices from '../../applyChoices';

const SIGNER_TITLE = {
  [languages.EN]: 'Signer',
  [languages.SV]: 'Signerare',
  [languages.DE]: 'Unterzeichner',
} as const;

const FIRST_NAME_LABEL = {
  [languages.SV]: 'Förnamn',
  [languages.EN]: 'First name',
  [languages.DE]: 'Vorname',
};

const LAST_NAME_LABEL = {
  [languages.SV]: 'Efternamn',
  [languages.EN]: 'Last name',
  [languages.DE]: 'Nachname',
};

const ROLE_LABEL = {
  [languages.SV]: 'Arbetsroll',
  [languages.EN]: 'Role',
  [languages.DE]: 'Funktion',
};

const PHONE_NUMBER_LABEL = {
  [languages.SV]: 'Telefonnummer',
  [languages.EN]: 'Phone Number',
  [languages.DE]: 'Telefonnummer',
};

const SIGNER_CONFIRM_LABEL = {
  [languages.SV]: 'Härmed försäkras att inlämnade uppgifter är riktiga och fullständiga.',
  [languages.EN]: 'Hereby it is certified that the provided information is accurate and complete.',
  [languages.DE]: 'Hiermit wird bestätigt, dass die angegebenen Informationen korrekt und vollständig sind.',
};

const ValueField = ({
  language,
  extraParameters,
  md = 6,
  boldFont = true,
}: {
  language?: Languages;
  extraParameters?: SignerParameters;
  md?: number;
  boldFont?: boolean;
}) => {
  const record = useRecordContext();

  const value = get(record, 'value');
  const sx = get(record, 'sx', {});

  if (!language) return null;

  const nationalIdentifierFields = extraParameters?.[questions.Q_NATIONAL_IDENTIFIER]?.fields;

  return (
    <>
      <Grid
        size={{
          xs: 12,
          md,
        }}
      >
        <Box display="flex" flexDirection="column">
          <Typography sx={sx} variant="label">
            {extraParameters?.fields?.confirmation?.label
            ?? SIGNER_CONFIRM_LABEL[language]}
          </Typography>
          <SelectField
            record={value}
            fontWeight={boldFont ? 'bold' : ''}
            choices={booleanChoices[language]}
            source="confirm"
            emptyText="-"
            sx={sx}
          />
        </Box>
      </Grid>
      {!extraParameters?.skipDetails && (
        <>
          <Grid
            size={{
              xs: 12,
              md,
            }}
          >
            <Box display="flex" flexDirection="column">
              <Typography sx={sx} variant="label">
                {extraParameters?.[questions.Q_FIRST_NAME]?.label
                  ?? FIRST_NAME_LABEL[language]}
              </Typography>
              <TextField
                record={value}
                fontWeight={boldFont ? 'bold' : ''}
                source="firstName"
                emptyText="-"
                sx={sx}
              />
            </Box>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md,
            }}
          >
            <Box display="flex" flexDirection="column">
              <Typography sx={sx} variant="label">
                {extraParameters?.[questions.Q_LAST_NAME]?.label
                  ?? LAST_NAME_LABEL[language]}
              </Typography>
              <TextField
                record={value}
                fontWeight={boldFont ? 'bold' : ''}
                source="lastName"
                emptyText="-"
                sx={sx}
              />
            </Box>
          </Grid>
          {value?.nationalIdentifier?.map((item: {
            country: string;
            identifier: string;
            type?: string;
          }) => (
            <Grid
              key={item.country}
              size={{
                xs: 12,
                md,
              }}
            >
              <Box display="flex" flexDirection="column">
                <Box gap={1} display="flex">
                  <Typography variant="label">National identifier for</Typography>
                  <SelectField
                    record={item}
                    source="country"
                    variant="label"
                    choices={applyChoices(
                      countryChoices(languages.EN),
                      nationalIdentifierFields?.countries?.options,
                      nationalIdentifierFields?.countries?.choiceLabels,
                    )}
                  />
                </Box>
                <Box display="flex" gap={2}>
                  <SelectField
                    record={item}
                    choices={applyChoices(
                      identifierTypeChoices(languages.EN),
                      nationalIdentifierFields?.type?.options,
                      nationalIdentifierFields?.type?.choiceLabels,
                    )}
                    source="type"
                    fontWeight={boldFont ? 'bold' : ''}
                  />
                  <Typography fontWeight={boldFont ? 'bold' : ''}>-</Typography>
                  <TextField
                    record={item}
                    source="identifier"
                    fontWeight={boldFont ? 'bold' : ''}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
          <Grid
            size={{
              xs: 12,
              md,
            }}
          >
            <Box display="flex" flexDirection="column">
              <Typography sx={sx} variant="label">{extraParameters?.fields?.role?.label ?? ROLE_LABEL[language]}</Typography>
              <TextField
                record={value}
                fontWeight={boldFont ? 'bold' : ''}
                source="role"
                emptyText="-"
                sx={sx}
              />
            </Box>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md,
            }}
          >
            <Box display="flex" flexDirection="column">
              <Typography sx={sx} variant="label">
                {extraParameters?.[questions.Q_EMAIL]?.label ?? 'Email'}
              </Typography>
              <TextField
                fontWeight={boldFont ? 'bold' : ''}
                record={value}
                source="email"
                emptyText="-"
                sx={sx}
              />
            </Box>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md,
            }}
          >
            <Box display="flex" flexDirection="column">
              <Typography sx={sx} variant="label">
                {extraParameters?.[questions.Q_PHONE_NUMBER]?.label
                  ?? PHONE_NUMBER_LABEL[language]}
              </Typography>
              <TextField
                record={value}
                fontWeight={boldFont ? 'bold' : ''}
                source="phoneNumber"
                emptyText="-"
                sx={sx}
              />
            </Box>
          </Grid>
        </>
      )}
    </>
  );
};

const Signer = ({
  extraParameters,
}: {
  extraParameters?: SignerParameters;
}) => {
  const record = useRecordContext();

  const language = 'en';

  const value = get(record, 'signer', get(record, 'value'));

  if (!value || !language) return <Typography>-</Typography>;

  return (
    <Answer
      label={extraParameters?.label ?? SIGNER_TITLE[language]}
      source="signer"
      datapoint={null}
      identificationInfo={undefined}
      isComplexType
    >
      <Card variant="outlined" sx={{ m: 0 }}>
        <CardContent sx={{ p: 4 }}>
          <Grid size={12}>
            <Grid container spacing={2}>
              <ValueField language={language} extraParameters={extraParameters} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Answer>
  );
};

Signer.ValueField = ({
  language,
  extraParameters,
}: {
  language?: Languages;
  extraParameters?: SignerParameters;
}) => (
  <Grid size={12}>
    <Grid container spacing={2}>
      <ValueField language={language} extraParameters={extraParameters} />
    </Grid>
  </Grid>
);

export default Signer;
